package nl.volleyplanner.ktor

import io.ktor.client.plugins.api.Send
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.utils.io.KtorDsl
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withLock
import nl.volleyplanner.ktor.RateLimiter
import kotlin.math.min
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

public val RateLimiterPlugin = createClientPlugin("rateLimiterPlugin", ::RateLimiterConfig) { pluginConfig
    val httpEngine = client.engine

    val rateLimiter = RateLimiter(
        pluginConfig.maxRequestsPerMinute,
        pluginConfig.minDurationBetweenRequestsMillis,
        pluginConfig.maxSimultaneousRequests,
        pluginConfig.clock
    )

    on(Send) { request ->
        /**
         * Skip rate limiting if we are reading from mock and the mock file exists
         * TODO: Make this configurable (still use rate limits to test production config)
         * TODO: Better way of configuring dependencies. But this is OK for now
         */
        if (httpEngine is MockingHttpClientEngine && httpEngine.mockConfig.useMock.readMock) {
            val mockFilePath = httpEngine.mockFileHandler.resolveMockFilePath(request.url.build())
            if (mockFilePath != null && httpEngine.mockFileHandler.mockFileSystem.exists(mockFilePath)) {
                return@on proceed(request)
            }
        }

        rateLimiter.acquire()

        try {
            proceed(request)
        } finally {
            rateLimiter.release()
        }
    }
}

@KtorDsl
class RateLimiterConfig(
    var maxRequestsPerMinute: Int = 1000,
    var minDurationBetweenRequestsMillis: Long = 0,
    var maxSimultaneousRequests: Int = 5,
    var clock: Clock = Clock.System
)

/**
 * Enables a Coroutine to rate-limit certain pieces of code
 *
 * Use the @see acquire() function to acquire a token. This is blocking until a token is available
 * Use the @see release() function to release a token
 */
@OptIn(ExperimentalTime::class)
class RateLimiter(
    private val maxRequestsPerMinute: Int,
    private val minDurationBetweenRequestsMillis: Long = 0,
    private val maxSimultaneousRequests: Int = 5,
    private val clock: Clock = Clock.System
) {
    private val maxTokens: Double = maxRequestsPerMinute.toDouble()
    private val refillRate: Double = maxRequestsPerMinute / 60.0 // tokens per second
    private var tokens: Double = maxTokens
    private var lastRefillTimestamp: Instant = clock.now()
    private var lastRequestTimestamp: Instant = clock.now()
    private val mutex = Mutex()
    private val semaphore = Semaphore(maxRequestsPerMinute)

    private suspend fun refillTokens() {
        val now = clock.now()
        val durationSinceLastRefill = (now - lastRefillTimestamp).inWholeNanoseconds
        val secondsSinceLastRefill = durationSinceLastRefill / 1_000_000_000.0
        tokens = min(maxTokens, tokens + secondsSinceLastRefill * refillRate)
        lastRefillTimestamp = now
    }

    suspend fun acquire() {
        semaphore.acquire()
        mutex.withLock {
            refillTokens()

            while (tokens < 1.0) {
                delay(100)
                refillTokens()
            }

            val now = clock.now()
            val timeSinceLastRequest = (now - lastRequestTimestamp).inWholeMilliseconds

            if (timeSinceLastRequest < minDurationBetweenRequestsMillis) {
                delay(minDurationBetweenRequestsMillis - timeSinceLastRequest)
            }

            tokens -= 1.0
            lastRequestTimestamp = clock.now()
        }
    }

    fun release() = semaphore.release()
}